/** XeAgents.ai **/
/**
 * Support Chat Hook
 *
 * Manages state and API interactions for the RAG-powered support chatbot.
 * Supports streaming responses via Server-Sent Events (SSE).
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthStorage } from "@/lib/auth-storage";

// Types
export interface SupportChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  confidence?: "high" | "medium" | "low" | "none";
  citations?: SupportChatCitation[];
  isStreaming?: boolean;
}

export interface SupportChatCitation {
  id: number;
  source: string;
  excerpt: string;
  relevance: number;
}

interface SupportChatMetadata {
  confidence: "high" | "medium" | "low" | "none";
  confidenceScore: number;
  hasAnswer: boolean;
  citations: SupportChatCitation[];
  suggestedAction?: "contact_support" | "refine_query" | null;
}

interface SupportChatStatus {
  enabled: boolean;
  chunkCount: number;
  message: string;
}

// Session storage key
const STORAGE_KEY = "support_chat_history";

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Load messages from session storage
function loadMessages(): SupportChatMessage[] {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
  } catch (e) {
    console.error("[SupportChat] Failed to load messages:", e);
  }
  return [];
}

// Save messages to session storage
function saveMessages(messages: SupportChatMessage[]): void {
  try {
    // Only save last 20 messages
    const toSave = messages.slice(-20);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.error("[SupportChat] Failed to save messages:", e);
  }
}

export function useSupportChat() {
  const [messages, setMessages] = useState<SupportChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load messages on mount
  useEffect(() => {
    const loaded = loadMessages();
    if (loaded.length > 0) {
      setMessages(loaded);
    }
  }, []);

  // Save messages when they change
  useEffect(() => {
    if (messages.length > 0) {
      saveMessages(messages);
    }
  }, [messages]);

  // Check if support chat is available
  const { data: status, isLoading: statusLoading } = useQuery<SupportChatStatus>({
    queryKey: ["/api/support-chat/status"],
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false
  });

  // Send message with streaming response
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    setError(null);
    setIsLoading(true);

    // Add user message
    const userMessage: SupportChatMessage = {
      id: generateId(),
      role: "user",
      content: content.trim(),
      timestamp: new Date()
    };

    // Add placeholder for assistant response
    const assistantMessageId = generateId();
    const assistantMessage: SupportChatMessage = {
      id: assistantMessageId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages(prev => [...prev, userMessage, assistantMessage]);

    // Prepare conversation history (last 8 messages)
    const conversationHistory = messages.slice(-8).map(m => ({
      role: m.role,
      content: m.content
    }));

    try {
      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();

      const authHeader = AuthStorage.getAuthHeader();
      const response = await fetch("/api/support-chat/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authHeader ? { Authorization: authHeader } : {})
        },
        body: JSON.stringify({
          message: content.trim(),
          conversationHistory
        }),
        credentials: "include",
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      // Process SSE stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let fullContent = "";
      let metadata: SupportChatMetadata | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            const eventType = line.slice(7).trim();

            // Get the next data line
            const dataLineIndex = lines.indexOf(line) + 1;
            if (dataLineIndex < lines.length && lines[dataLineIndex].startsWith("data: ")) {
              const dataStr = lines[dataLineIndex].slice(6);
              try {
                const data = JSON.parse(dataStr);

                if (eventType === "metadata") {
                  metadata = data;
                } else if (eventType === "text" && data.content) {
                  fullContent += data.content;
                  // Update message content
                  setMessages(prev =>
                    prev.map(m =>
                      m.id === assistantMessageId
                        ? { ...m, content: fullContent }
                        : m
                    )
                  );
                } else if (eventType === "error") {
                  throw new Error(data.error || "Stream error");
                }
              } catch (e) {
                // Ignore parse errors for partial data
              }
            }
          } else if (line.startsWith("data: ")) {
            // Handle data without event prefix (for simpler parsing)
            const dataStr = line.slice(6);
            try {
              const data = JSON.parse(dataStr);
              if (data.content) {
                fullContent += data.content;
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMessageId
                      ? { ...m, content: fullContent }
                      : m
                  )
                );
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }

      // Finalize the message
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMessageId
            ? {
                ...m,
                content: fullContent || "I apologize, but I couldn't generate a response. Please try again.",
                isStreaming: false,
                confidence: metadata?.confidence,
                citations: metadata?.citations
              }
            : m
        )
      );

    } catch (err: any) {
      if (err.name === "AbortError") {
        // Request was cancelled
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMessageId
              ? { ...m, content: "Response cancelled.", isStreaming: false }
              : m
          )
        );
      } else {
        console.error("[SupportChat] Error:", err);
        setError(err.message || "Failed to get response");
        // Update the assistant message with error
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  content: "I'm sorry, I encountered an error. Please try again or create a support ticket for assistance.",
                  isStreaming: false,
                  confidence: "none" as const
                }
              : m
          )
        );
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [messages, isLoading]);

  // Cancel ongoing request
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Clear chat history
  const clearHistory = useCallback(() => {
    setMessages([]);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  // Get last confidence level
  const lastConfidence = messages.length > 0
    ? messages.filter(m => m.role === "assistant").slice(-1)[0]?.confidence
    : null;

  // Get last citations
  const lastCitations = messages.length > 0
    ? messages.filter(m => m.role === "assistant").slice(-1)[0]?.citations || []
    : [];

  return {
    messages,
    isLoading,
    error,
    isEnabled: status?.enabled ?? false,
    statusLoading,
    sendMessage,
    cancelRequest,
    clearHistory,
    lastConfidence,
    lastCitations
  };
}
